import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { apiGet, apiPost } from '../lib/http.js';
import { UNAUTHORIZED_EVENT } from '../lib/api.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigateRef = useRef(null);

  // The router owns navigation. We expose a setter so App.jsx (or main.jsx)
  // can install a `navigate(path)` once. Keeping it as a ref avoids re-renders
  // triggered by react-router identity changes.
  const setNavigator = useCallback((fn) => {
    navigateRef.current = fn;
  }, []);

  const navigate = useCallback((path) => {
    if (navigateRef.current) navigateRef.current(path);
    else if (typeof window !== 'undefined') window.location.assign(path);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function checkSession() {
      try {
        // silent: 401 here just means "no session yet" — it must NOT trigger
        // the global unauthorized listener that would bounce the user to /login
        // on every page reload of a public route.
        const me = await apiGet('/api/auth/me', { silent: true });
        if (!cancelled) setUser(me);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    checkSession();

    function onUnauthorized() {
      setUser(null);
      navigate('/login');
    }

    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => {
      cancelled = true;
      window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    };
  }, [navigate]);

  const login = useCallback(
    async (username, password) => {
      await apiPost('/api/auth/login', { username, password });
      const me = await apiGet('/api/auth/me');
      setUser(me);
      navigate('/admin');
    },
    [navigate],
  );

  const logout = useCallback(async () => {
    try {
      await apiPost('/api/auth/logout');
    } finally {
      setUser(null);
      navigate('/login');
    }
  }, [navigate]);

  const value = { user, loading, login, logout, setNavigator };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth debe usarse dentro de un <AuthProvider>');
  }
  return ctx;
}
